import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';

import ContentTypography from 'components/ContentTypography';
import FullHeightGrid from 'components/FullHeightGrid';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Toolbar from '@material-ui/core/Toolbar';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';

import ResponsiveImg from 'components/ResponsiveImg';

const CTAImg = styled(ResponsiveImg)`
  width: 100%;
  height: 100%;
  img {
    width: 100%;
  }
`;

const UnstyledLink = styled(Link)`
  text-decoration: none;
`;

const InlineCard = styled(Card)`
  ${props =>
    !props.noGutter &&
    css`
      margin-top: 1em;
      margin-bottom: 1em;
    `}
  ${props =>
    props.size === 'slim' &&
    css`
      max-width: 100%;

      @media (min-width: 480px) {
        float: right;
        margin: 1em 0 1em 2em;
        max-width: 320px;
      }
    `};
  ${props =>
    props.size === 'slim' &&
    props.overflow &&
    css`
      @media (min-width: 960px) {
        margin-right: -15%;
      }
    `};
  ${props =>
    props.size === 'wide' &&
    props.overflow &&
    css`
      @media (min-width: 960px) {
        margin-right: -15%;
        margin-left: -15%;
      }
    `};
  ${props =>
    props.size === 'wide' &&
    css`
      ${CTAImg} {
        @media (min-width: 600px) {
          padding: 16px;
          img {
            border-radius: 5px;
          }
        }
      }
    `}
`;

const InlineCTA = ({
  heading,
  text,
  link,
  image,
  size,
  cardTitle,
  overflow,
  alignItems,
  ...other
}) => (
  <InlineCard size={size} overflow={overflow ? 1 : 0} {...other}>
    {cardTitle && (
      <Fragment>
        <Toolbar>
          <Typography variant="h6" noWrap>
            {cardTitle}
          </Typography>
        </Toolbar>
        <Divider />
      </Fragment>
    )}
    <FullHeightGrid container alignItems={alignItems || 'center'}>
      {image && (
        <Grid
          item
          xs={12}
          sm={size === 'wide' ? 5 : null}
          md={size === 'wide' ? 3 : null}
        >
          <CTAImg src={image.url} maxWidth={640} square />
        </Grid>
      )}
      <Grid item xs={12} sm>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {heading}
          </Typography>
          <ContentTypography
            gutterBottom
            variant="body1"
            dangerouslySetInnerHTML={{ __html: text }}
          />
          <Button
            href={link?.href}
            color="primary"
            variant="raised"
            fullWidth={size === 'wide' ? false : true}
            target="_blank"
            disableRipple
          >
            {link?.title}
          </Button>
        </CardContent>
      </Grid>
    </FullHeightGrid>
  </InlineCard>
);

const InlineResourceCTA = ({ resource, title, ...other }) =>
  resource ? (
    <InlineCTA
      cardTitle={title}
      image={resource.main_image}
      heading={resource.title}
      text={resource.description}
      link={{ href: resource.url, title: resource.top_cta_button_label }}
      {...other}
    />
  ) : null;

const InlineBlogCTA = ({ title, post, size, overflow, alignItems, ...other }) =>
  post ? (
    <InlineCard size={size} overflow={overflow ? 1 : 0} {...other}>
      {title && (
        <Fragment>
          <Toolbar>
            <Typography variant="h5" noWrap>
              {title}
            </Typography>
          </Toolbar>
          <Divider />
        </Fragment>
      )}
      <FullHeightGrid container alignItems={alignItems || 'center'}>
        <Grid
          item
          xs={12}
          sm={size === 'wide' ? 5 : null}
          md={size === 'wide' ? 3 : null}
        >
          {post.hero_image && (
            <Link to={post.url}>
              <CTAImg
                src={post.hero_image.url}
                alt="Blog Image"
                maxWidth={640}
                square
              />
            </Link>
          )}
        </Grid>
        <Grid item xs={12} sm>
          <UnstyledLink to={post.url}>
            <CardContent>
              <Typography variant="overline" component="p" gutterBottom>
                {post.blog_category?.length > 0 && (
                  <span>{post.blog_category[0].title}</span>
                )}
              </Typography>
              <Typography gutterBottom variant="h5">
                {post.title}
              </Typography>
              {post.excerpt && (
                <Typography variant="body1" component="p">
                  {post.excerpt}
                </Typography>
              )}
            </CardContent>
          </UnstyledLink>
        </Grid>
      </FullHeightGrid>
    </InlineCard>
  ) : null;

InlineResourceCTA.defaultProps = {
  size: 'wide',
  overflow: false
};

InlineResourceCTA.propTypes = {
  title: PropTypes.string,
  resource: PropTypes.object.isRequired,
  size: PropTypes.string,
  overflow: PropTypes.bool
};

InlineBlogCTA.defaultProps = {
  size: 'wide',
  overflow: false
};

InlineBlogCTA.propTypes = {
  title: PropTypes.string,
  post: PropTypes.object.isRequired,
  size: PropTypes.string,
  overflow: PropTypes.bool
};

export { InlineCTA, InlineResourceCTA, InlineBlogCTA };
